import { apiClient } from './api';

export interface CallLeadInfo {
  leadId: string | null;
  leadPhone: string | null;
  leadName: string | null;
  leadEmail: string | null;
  companyName: string | null;
  leadNotes: string | null;
  campaignName: string | null;
  campaignId: string | null;
  callAttemptId: string | null;
  scriptName?: string | null;
  scriptContent?: string | null;
  scriptStructure?: Record<string, any> | null;
  /** Pipeline main stage before/during call (e.g. interested, follow_up) */
  leadStatus?: string | null;
  /** Raw lead.status before dial (new, called, interested, …) */
  preDialStatus?: string | null;
  subStageId?: string | null;
  dispositionMode?: 'results_callback' | 'main_dial' | 'full';
  /** True when this inbound is a live merchant attach (accept during wrap-up). */
  liveConnect?: boolean;
}

/**
 * What `/agents/status` and its SSE stream actually send, per agent.
 *
 * Declared here because it was previously typed as a bare status string, which
 * is not what the API returns — the admin table then used the object as a
 * lookup key, every agent resolved to "Offline", and TypeScript had no way to
 * notice because the lie was in the declaration itself.
 */
export interface AgentStatusInfo {
  status: 'available' | 'ringing' | 'in-call' | 'wrap-up' | 'offline';
  leadName?: string | null;
  companyName?: string | null;
  hangupBy?: string | null;
}

export interface AgentHeartbeatResult {
  /** Server is asking this browser to establish a parked leg. */
  shouldPark: boolean;
  /** The leg the browser reported is not the one the server knows about — hang it up. */
  dropLeg?: boolean;
  /** Current park state, when the server has a session for this agent. */
  state?: 'idle' | 'reserved' | 'bridged' | 'wrapup';
  park?: {
    destinationNumber: string;
    callerNumber: string;
    agentId: string;
  };
}

export interface VoiceTokenResponse {
  token: string;
  identity: string;
  provider: 'telnyx';
  credentialId: string;
}

export interface AgentStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalCalls: number;
  completedCalls: number;
  failedCalls: number;
  todayCalls: number;
  avgDuration: number;
  totalDuration: number;
  routedCalls: number;
  inProgressCalls: number;
  totalLeads: number;
}

export interface AgentCampaign {
  id: string;
  name: string;
  status: string;
  campaign_type: string;
  dialing_type?: 'auto' | 'manual';
  call_provider: string;
  outboundNumbers: string[];
  stats: { total: number; completed: number };
  totalLeads: number;
  created_at: string;
  started_at?: string;
}

export interface AgentCallActivity {
  leg_id: string;
  leg_status: string;
  routed_at: string;
  call_attempt_id: string;
  call_status: string;
  call_reason?: string;
  duration_seconds?: number;
  started_at?: string;
  finished_at?: string;
  lead_id?: string;
  lead_name?: string;
  lead_phone?: string;
  lead_status?: string;
  campaign_name?: string;
  campaign_id?: string;
}

export const agentService = {
  async getMyStats(): Promise<AgentStats> {
    const response = await apiClient.get<AgentStats>('/agents/my-stats');
    return response.data.data;
  },

  async getVoiceToken(): Promise<VoiceTokenResponse> {
    const response = await apiClient.post<VoiceTokenResponse>(
      '/agents/voice-token'
    );
    return response.data.data;
  },

  async getAgentStatuses(agentIds: string[]): Promise<Record<string, AgentStatusInfo>> {
    const response = await apiClient.post<{ statuses: Record<string, AgentStatusInfo> }>(
      '/agents/status',
      { agentIds }
    );
    return response.data.data.statuses;
  },
  async getCallInfo(callControlId: string): Promise<CallLeadInfo> {
    const response = await apiClient.get<CallLeadInfo>(
      '/agents/call-info',
      { params: { callSid: callControlId } }
    );
    return response.data.data;
  },
  async getLeadByPhone(phone: string): Promise<CallLeadInfo> {
    const response = await apiClient.get<CallLeadInfo>(
      '/agents/lead-by-phone',
      { params: { phone } }
    );
    return response.data.data;
  },
  /** Most reliable lookup: find lead by agent's current routing leg (no phone/callSid needed) */
  async getMyCurrentLead(): Promise<CallLeadInfo> {
    const response = await apiClient.get<CallLeadInfo>('/agents/my-current-lead');
    return response.data.data;
  },
  /** Check if current call has voicemail/no-answer alerts or bridge established */
  async checkCallAlert(callAttemptId?: string): Promise<{ voicemailDetected: boolean; callAttemptId?: string; callBridged?: boolean; noAnswerDetected?: boolean; callEnded?: boolean; screeningDetected?: boolean }> {
    const response = await apiClient.get<{ voicemailDetected: boolean; callAttemptId?: string; callBridged?: boolean; noAnswerDetected?: boolean; callEnded?: boolean; screeningDetected?: boolean }>('/agents/me/call-alert', {
      params: {
        ...(callAttemptId ? { callAttemptId } : {}),
        _t: Date.now(),
      },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      // Background alert poll — transient failures are handled by the caller's
      // try/catch and SSE fallback; never surface a "Network error" toast for it.
      skipErrorToast: true,
    });
    return response.data.data;
  },
  getCallAlertStreamUrl(callAttemptId?: string): string {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const fullUrl = baseUrl.startsWith('http')
      ? `${baseUrl}/agents/me/call-alert/stream`
      : `${window.location.origin}${baseUrl}/agents/me/call-alert/stream`;
    const url = new URL(fullUrl);
    if (token) url.searchParams.set('token', token);
    if (callAttemptId) url.searchParams.set('callAttemptId', callAttemptId);
    return url.toString();
  },
  async setDisposition(params: {
    leadId: string | null;
    callAttemptId: string | null;
    disposition: string;
    notes?: string;
    followUpDate?: string;
    subStageId?: string;
    mainStatusChange?: string;
    followUpOnly?: boolean;
    callHistoryOnly?: boolean;
    autoDisposition?: boolean;
  }): Promise<void> {
    await apiClient.post('/agents/disposition', params);
  },
  async setDispositionPending(pending: boolean): Promise<void> {
    await apiClient.put('/agents/me/disposition-pending', { pending });
  },
  async getMyCampaigns(pipelineStatus?: string): Promise<AgentCampaign[]> {
    const params = pipelineStatus ? { pipelineStatus } : undefined;
    const response = await apiClient.get<AgentCampaign[]>('/agents/my-campaigns', { params });
    return response.data.data;
  },

  async getTagOptions(): Promise<Array<{ id: string; name: string; email?: string }>> {
    const response = await apiClient.get('/agents/tag-options');
    return response.data.data;
  },
  async getMyCalls(limit?: number): Promise<AgentCallActivity[]> {
    const response = await apiClient.get<AgentCallActivity[]>('/agents/my-calls', {
      params: { limit: limit || 20 },
    });
    return response.data.data;
  },
  async getMyCallsHistory(params: { page?: number; limit?: number; search?: string; status?: string }): Promise<{
    calls: AgentCallActivity[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const response = await apiClient.get('/agents/my-calls-history', { params });
    return response.data.data;
  },
  async getMyResults(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    campaignId?: string;
    timezone?: string;
    subStageId?: string;
    followUpDateFrom?: string;
    followUpDateTo?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    assignedOnly?: boolean;
    callAttempts?: string;
  }): Promise<{
    leads: any[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    tabCounts: Record<string, number>;
    subStageCounts?: Record<string, number>;
    selectionMetadata?: {
      timezoneFilterApplied: boolean;
      areaCodePriorityApplied: boolean;
      activeTimezones: string[];
      dbTimezoneValues: string[];
      etStatsDate: string;
      source?: 'manual_override' | 'campaign_timezone' | 'none';
      effectiveTimezone?: 'Eastern' | 'Central' | 'Mountain' | 'Pacific' | 'Unknown' | null;
    };
  }> {
    const response = await apiClient.get('/agents/my-results', { params });
    return response.data.data;
  },
  async resetLeadNumberAttempts(leadId: string, campaignId: string): Promise<{ resetApplied: boolean }> {
    const response = await apiClient.post('/agents/reset-lead-number-attempts', { leadId, campaignId });
    return response.data.data;
  },
  async getAvailability(): Promise<boolean> {
    const response = await apiClient.get<{ isAvailable: boolean }>('/agents/availability');
    return response.data.data.isAvailable;
  },
  async setAvailability(
    isAvailable: boolean,
    parkCallId?: string | null
  ): Promise<{ isAvailable: boolean } & AgentHeartbeatResult> {
    const response = await apiClient.put<{ isAvailable: boolean } & AgentHeartbeatResult>(
      '/agents/availability',
      { isAvailable, ...(parkCallId ? { parkCallId } : {}) }
    );
    return response.data.data;
  },
  /**
   * Presence ping, and the reconciliation point for a parked agent leg.
   *
   * The browser reports the park leg it believes it holds; the server decides
   * whether that leg is the one it knows about, and answers with what to do.
   * The rollout flag lives on the server alone — a browser never decides to
   * park, it is told to, and told what to dial.
   */
  async sendHeartbeat(
    parkCallId?: string | null,
    rtcStatus?: string | null
  ): Promise<AgentHeartbeatResult> {
    // The heartbeat proves the tab is alive; rtcStatus proves the WebRTC client
    // is registered and could take a call. Those come apart, and the server
    // cannot tell the difference without being told.
    const response = await apiClient.post<AgentHeartbeatResult>('/agents/heartbeat', {
      ...(parkCallId ? { parkCallId } : {}),
      ...(rtcStatus ? { rtcStatus } : {}),
    });
    return response.data.data ?? { shouldPark: false };
  },

  /**
   * File what this browser could hear on a call that has just ended.
   *
   * The only place the answer exists. An agent who sat in silence cannot say
   * whether the line was dead or the media never reached them, and neither can
   * anything on the server — the inbound-rtp report is browser-side and lives
   * as long as the call does. Sent once, after the fact, and deliberately not
   * awaited by anything: it is evidence, and a call must not be held up for it.
   */
  async reportCallAudio(callAttemptId: string, state: string): Promise<void> {
    await apiClient.post('/agents/call-audio', { callAttemptId, state });
  },

  /**
   * Say why this client refused a transfer.
   *
   * A refusal reaches the merchant as SIP 486 and ends their call, so it is the
   * most expensive thing the browser decides on its own — and the only record
   * of it was a console line in one agent's tab. Reports the inputs to the
   * decision, not a verdict, so the server can see which guard fired.
   */
  async reportClientReject(detail: {
    reason: string;
    incomingCallId: string | null;
    incomingCallState: string | null;
    existingCallId: string | null;
    existingCallState: string | null;
    existingReadsAsUp: boolean;
    parkCallId: string | null;
    parkLegUp: boolean;
    status: string | null;
    awaitingDisposition: boolean;
  }): Promise<void> {
    await apiClient.post('/agents/client-reject', detail);
  },

  /** Build SSE URL for team status stream (agent dashboard) */
  getTeamStatusStreamUrl(): string {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const fullUrl = baseUrl.startsWith('http')
      ? `${baseUrl}/agents/team-status/stream`
      : `${window.location.origin}${baseUrl}/agents/team-status/stream`;
    const url = new URL(fullUrl);
    if (token) url.searchParams.set('token', token);
    return url.toString();
  },

  /** Manually dial a lead (manual dialing campaigns only) */
  async dialLead(leadId: string, campaignId: string): Promise<{
    callAttemptId: string;
    callControlId: string;
    routingAttemptId: string;
  }> {
    const response = await apiClient.post('/agents/dial-lead', { leadId, campaignId });
    return response.data.data;
  },

  /** Prepare a manual dial: validate + create records, return data for WebRTC direct call */
  async prepareDial(
    leadId: string,
    campaignId: string,
    options?: { resultsCallback?: boolean }
  ): Promise<{
    callAttemptId: string;
    routingAttemptId: string;
    routingLegId: string | null;
    fromNumber: string;
    toNumber: string;
    leadId: string;
    leadName: string | null;
    leadPhone: string | null;
    leadEmail: string | null;
    companyName: string | null;
    campaignId: string;
    campaignName: string | null;
    scriptName: string | null;
    scriptContent: string | null;
    scriptStructure?: Record<string, any> | null;
    agentId: string;
    leadPhoneNumberId?: string;
    phoneNumberCount?: number;
    activePhoneNumberCount?: number;
    leadStatus?: string | null;
    preDialStatus?: string | null;
    subStageId?: string | null;
  }> {
    const response = await apiClient.post('/agents/prepare-dial', {
      leadId,
      campaignId,
      ...(options?.resultsCallback ? { resultsCallback: true } : {}),
    });
    return response.data.data;
  },

  /** Cancel / end a manually dialed call (can work with just leadId). Optionally set a disposition. */
  async cancelDial(options: {
    callControlId?: string;
    callAttemptId?: string;
    leadId?: string;
    disposition?: string;
    autoDisposition?: boolean;
  }): Promise<void> {
    await apiClient.post('/agents/cancel-dial', options, { timeout: 10000 });
  },

  async clearStuckCall(): Promise<{
    clearedCallAttempts: number;
    clearedRoutingAttempts: number;
    clearedRoutingLegs: number;
    resetLeads: number;
    releasedAgentLock: boolean;
    clearedManualLocks: number;
  }> {
    const response = await apiClient.post<{
      clearedCallAttempts: number;
      clearedRoutingAttempts: number;
      clearedRoutingLegs: number;
      resetLeads: number;
      releasedAgentLock: boolean;
      clearedManualLocks: number;
    }>('/agents/clear-stuck-call');
    return response.data.data;
  },

  /** REST fallback for lead dial status (used when SSE disconnects) */
  async getLeadDialStatus(campaignId: string): Promise<any> {
    const response = await apiClient.get('/agents/lead-dial-status', { params: { campaignId } });
    return response.data;
  },

  /** Build SSE URL for lead dial status stream (manual dialing) */
  getLeadDialStatusStreamUrl(campaignId: string): string {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const fullUrl = baseUrl.startsWith('http')
      ? `${baseUrl}/agents/lead-dial-status/stream`
      : `${window.location.origin}${baseUrl}/agents/lead-dial-status/stream`;
    const url = new URL(fullUrl);
    if (token) url.searchParams.set('token', token);
    url.searchParams.set('campaignId', campaignId);
    return url.toString();
  },
  getMyResultsStreamUrl(campaignId?: string): string {
    const token = localStorage.getItem('accessToken');
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const fullUrl = baseUrl.startsWith('http')
      ? `${baseUrl}/agents/my-results/stream`
      : `${window.location.origin}${baseUrl}/agents/my-results/stream`;
    const url = new URL(fullUrl);
    if (token) url.searchParams.set('token', token);
    if (campaignId) url.searchParams.set('campaignId', campaignId);
    return url.toString();
  },
};
